import React, { useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import SearchIcon from "@mui/icons-material/Search";
import {
  DatePicker,
  LocalizationProvider,
} from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { useGetCustomerScheduleReportQuery } from "../store/services/reportApi";
import { useGetrcsresellerschedulereportQuery } from "../store/services/rcstemplateApi";
import { cellSX, layoutCSS } from "../component/common/commonStyle";

const ResellerScheduledSMS = () => {
  const [startDate, setStartDate] = useState(dayjs());
  const [endDate, setEndDate] = useState(dayjs());
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [serviceType, setServiceType] = useState("sms");
  const [shouldFetch, setShouldFetch] = useState(false);
  const [selectedText, setSelectedText] = useState("");
  const [textDialogOpen, setTextDialogOpen] = useState(false);

  const startdate = startDate?.format("YYYY-MM-DD");
  const enddate = endDate?.format("YYYY-MM-DD");
  const {
    currentData: reportData,
    isFetching,
    isError,
  } = useGetCustomerScheduleReportQuery(
    { startdate, enddate, page, limit: rowsPerPage },
    { skip: !shouldFetch || serviceType !== "sms" },
  );
  const {
    currentData: rcsReportData,
    isFetching: isRcsFetching,
    isError: isRcsError,
  } = useGetrcsresellerschedulereportQuery(
    { startdate, enddate, page, limit: rowsPerPage },
    { skip: !shouldFetch || serviceType !== "rcs" },
  );

  const handleSearch = () => {
    setPage(1);
    setShouldFetch(true);
  };

  const handlePageChange = (_, value) => {
    setPage(value);
  };

  const rows = serviceType === "sms" ? reportData?.data ?? [] : [];
  const rcsRows = serviceType === "rcs" ? rcsReportData?.data ?? [] : [];
  const totalPages =
    serviceType === "rcs"
      ? rcsReportData?.pagination?.totalPages ?? 0
      : reportData?.pagination?.totalPages ?? 0;
  const totalRecords =
    serviceType === "rcs"
      ? rcsReportData?.pagination?.totalRecords ?? 0
      : reportData?.pagination?.totalRecords ?? 0;
  const headings =
    serviceType === "rcs"
      ? [
          "Date",
          "Sr No",
          "Campaign ID",
          "Campaign Name",
          "Customer",
          "Recipients",
          "Template",
          "Bot",
          "Operator",
          "Category / Type",
          "Status",
          "Created At",
          "Message",
        ]
      : [
          "Date",
          "Campaign ID",
          "Customer",
          "Total Numbers",
          "Msg Count",
          "Total Count",
          "Template Name",
          "SenderID",
          "Status",
          "Uploaded At",
        ];

  const handleViewText = (text) => {
    setSelectedText(text || "");
    setTextDialogOpen(true);
  };

  const inputSx = {
    borderRadius: "0.5rem",
    minWidth: 100,
    "& .MuiOutlinedInput-root": {
      background: "rgba(255,255,255,0.7)",
      "& fieldset": { borderColor: "#E2E8F0" },
      "&:hover fieldset": { borderColor: "#94A3B8" },
    },
    "& .MuiInputLabel-root": { color: "#475569" },
    "& .Mui-disabled": {
      "& .MuiOutlinedInput-input": {
        color: "#334155 !important",
        WebkitTextFillColor: "#334155 !important",
      },
      "& .MuiOutlinedInput-notchedOutline": { borderColor: "#CBD5E1" },
    },
  };

  return (
    <Box sx={layoutCSS}>
      <Typography
        variant="h6"
        sx={{ mb: 1, fontSize: "0.9rem", fontWeight: "bold" }}
      >
        Reseller Scheduled Campaign Report
      </Typography>

      <Box
        sx={{
          display: "flex",
          gap: 1,
          flexWrap: "wrap",
          alignItems: "center",
          borderRadius: "0.5rem",
          p: 2,
          backgroundColor: "#ffffff52",
          backdropFilter: "blur(10px)",
          boxShadow: "0 4px 12px rgba(14,165,233,0.1)",
          mb: 2,
        }}
      >
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            label="Start Date"
            value={startDate}
            format="DD/MM/YYYY"
            maxDate={dayjs()}
            sx={{ ...inputSx, width: 140 }}
            slotProps={{ textField: { size: "small" } }}
            onChange={(value) => {
              setStartDate(value);
              setPage(1);
              if (endDate && value && endDate.isBefore(value)) {
                setEndDate(value);
              } else if (
                endDate &&
                value &&
                endDate.diff(value, "month", true) > 1
              ) {
                setEndDate(value.add(1, "month"));
              }
            }}
          />
          <DatePicker
            label="End Date"
            value={endDate}
            format="DD/MM/YYYY"
            sx={{ ...inputSx, width: 140 }}
            maxDate={
              startDate
                ? startDate.add(1, "month").isBefore(dayjs())
                  ? startDate.add(1, "month")
                  : dayjs()
                : dayjs()
            }
            minDate={startDate || undefined}
            slotProps={{ textField: { size: "small" } }}
            onChange={(value) => {
              setEndDate(value);
              setPage(1);
            }}
          />
        </LocalizationProvider>

        <TextField
          select
          label="Report Type"
          value={serviceType}
          onChange={(event) => setServiceType(event.target.value)}
          size="small"
          sx={{ ...inputSx, width: 140 }}
        >
          {[
            ["sms", "SMS"],
            ["rcs", "RCS"],
            ["clicker", "Clicker"],
            ["whatsapp", "WhatsApp"],
          ].map(([value, label]) => (
            <MenuItem key={value} value={value} sx={{ fontSize: "0.75rem" }}>
              {label}
            </MenuItem>
          ))}
        </TextField>

        <Button
          variant="contained"
          startIcon={<SearchIcon />}
          onClick={handleSearch}
          sx={{ height: 35, borderRadius: "8px" }}
        >
          Search
        </Button>
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1,
        }}
      >
        <Typography variant="body2" sx={{ fontSize: "0.75rem" }}>
          Showing {rowsPerPage} records per page
        </Typography>
        <FormControl size="small" sx={{ minWidth: 80 }}>
          <Select
            value={rowsPerPage}
            onChange={(event) => {
              setRowsPerPage(Number(event.target.value));
              setPage(1);
            }}
            sx={inputSx}
          >
            {[25, 50, 100].map((count) => (
              <MenuItem key={count} value={count}>
                {count}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <TableContainer component={Paper} sx={{ borderRadius: 0.3 }}>
        <Table size="small">
          <TableHead
            sx={{
              bgcolor: "#0288d1",
              "& th": {
                color: "white",
                fontSize: "0.78rem",
                padding: "7px 8px",
                borderBottom: "0.5px solid #1b1b1b",
                borderRight: "0.5px solid #1b1b1b",
                whiteSpace: "nowrap",
              },
            }}
          >
            <TableRow>
              {headings.map((heading) => (
                <TableCell key={heading}>{heading}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {serviceType !== "sms" && serviceType !== "rcs" ? (
              <TableRow>
                <TableCell colSpan={headings.length} align="center" sx={cellSX}>
                  This report currently supports SMS and RCS only.
                </TableCell>
              </TableRow>
            ) : !shouldFetch ? (
              <TableRow>
                <TableCell colSpan={headings.length} align="center" sx={cellSX}>
                  Click Search to load data
                </TableCell>
              </TableRow>
            ) : (serviceType === "sms" ? isFetching : isRcsFetching) ? (
              <TableRow>
                <TableCell colSpan={headings.length} align="center" sx={cellSX}>
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : (serviceType === "sms" ? isError : isRcsError) ? (
              <TableRow>
                <TableCell colSpan={headings.length} align="center" sx={cellSX}>
                  Error loading data
                </TableCell>
              </TableRow>
            ) : serviceType === "rcs" ? (
              rcsRows.length ? (
                rcsRows.map((row) => (
                  <TableRow key={row.campaign_id} hover>
                    <TableCell sx={cellSX}>
                      {row.scheduled_time &&
                      dayjs(row.scheduled_time).isValid() ? (
                        <>
                          <Typography
                            variant="caption"
                            display="block"
                            fontWeight={600}
                            sx={{ fontSize: "0.72rem", lineHeight: 1.4 }}
                          >
                            {dayjs(row.scheduled_time).format("DD/MM/YYYY")}
                          </Typography>
                          <Typography
                            variant="caption"
                            display="block"
                            color="text.secondary"
                            sx={{ fontSize: "0.68rem", lineHeight: 1.4 }}
                          >
                            {dayjs(row.scheduled_time).format("HH:mm:ss")}
                          </Typography>
                        </>
                      ) : (
                        "—"
                      )}
                    </TableCell>
                    <TableCell sx={cellSX}>{row.sr_no ?? "—"}</TableCell>
                    <TableCell sx={cellSX}>{row.campaign_id ?? "—"}</TableCell>
                    <TableCell sx={cellSX}>{row.campaign_name || "—"}</TableCell>
                    <TableCell sx={cellSX}>
                      {row.username || row.customer_id || "—"}
                    </TableCell>
                    <TableCell sx={cellSX}>
                      {row.total_recipients ?? "—"}
                    </TableCell>
                    <TableCell sx={cellSX}>
                      {row.template_name || "—"}
                    </TableCell>
                    <TableCell sx={cellSX}>{row.BotName || "—"}</TableCell>
                    <TableCell sx={cellSX}>{row.operator || "—"}</TableCell>
                    <TableCell sx={cellSX}>
                      {[row.category, row.MessageType]
                        .filter(Boolean)
                        .join(" / ") || "—"}
                    </TableCell>
                    <TableCell sx={cellSX}>
                      <Chip
                        label={row.status || "Pending"}
                        size="small"
                        color={
                          row.status?.toLowerCase() === "completed"
                            ? "success"
                            : row.status?.toLowerCase() === "failed"
                            ? "error"
                            : "warning"
                        }
                        sx={{
                          height: 20,
                          fontSize: "0.65rem",
                          "& .MuiChip-label": { px: 1 },
                        }}
                      />
                    </TableCell>
                    <TableCell sx={cellSX}>
                      {row.created_at && dayjs(row.created_at).isValid()
                        ? dayjs(row.created_at).format("DD/MM/YYYY HH:mm:ss")
                        : "—"}
                    </TableCell>
                    <TableCell sx={cellSX}>
                      {row.TemplateText ? (
                        <Tooltip title="View RCS message">
                          <IconButton
                            size="small"
                            onClick={() => handleViewText(row.TemplateText)}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        "—"
                      )}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={headings.length}
                    align="center"
                    sx={cellSX}
                  >
                    No data found for the selected criteria
                  </TableCell>
                </TableRow>
              )
            ) : rows.length ? (
              rows.map((row, index) => (
                <TableRow key={row.smsupload_id || index} hover>
                  <TableCell sx={cellSX}>
                    {row.time_to_send && dayjs(row.time_to_send).isValid() ? (
                      <>
                        <Typography
                          variant="caption"
                          display="block"
                          fontWeight={600}
                          sx={{ fontSize: "0.72rem", lineHeight: 1.4 }}
                        >
                          {dayjs(row.time_to_send).format("DD/MM/YYYY")}
                        </Typography>
                        <Typography
                          variant="caption"
                          display="block"
                          color="text.secondary"
                          sx={{ fontSize: "0.68rem", lineHeight: 1.4 }}
                        >
                          {dayjs(row.time_to_send).format("HH:mm:ss")}
                        </Typography>
                      </>
                    ) : (
                      "—"
                    )}
                  </TableCell>
                  <TableCell sx={cellSX}>{row.smsupload_id || "—"}</TableCell>
                  <TableCell sx={cellSX}>
                    {row.customer_name || row.username || row.customer_id || "—"}
                  </TableCell>
                  <TableCell sx={cellSX}>{row.total_mobiles ?? "—"}</TableCell>
                  <TableCell sx={cellSX}>{row.msg_count ?? "—"}</TableCell>
                  <TableCell sx={cellSX}>{row.total_count ?? "—"}</TableCell>
                  <TableCell sx={cellSX}>
                    {row.template_name || "—"}
                    <Tooltip title="View SMS Text">
                      <IconButton
                        size="small"
                        onClick={() => handleViewText(row.text)}
                        sx={{ ml: 0.5 }}
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                  <TableCell sx={cellSX}>{row.senderID || "—"}</TableCell>
                  <TableCell sx={cellSX}>
                    <Chip
                      label={row.file_status || "Pending"}
                      size="small"
                      color={
                        row.file_status?.toLowerCase() === "completed"
                          ? "success"
                          : row.file_status?.toLowerCase() === "failed"
                          ? "error"
                          : "warning"
                      }
                      sx={{
                        height: 20,
                        fontSize: "0.65rem",
                        "& .MuiChip-label": { px: 1 },
                      }}
                    />
                  </TableCell>
                  <TableCell sx={cellSX}>
                    {row.uploadtime && dayjs(row.uploadtime).isValid()
                      ? dayjs(row.uploadtime).format("DD/MM/YYYY HH:mm:ss")
                      : "—"}
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={10} align="center" sx={cellSX}>
                  No data found for the selected criteria
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        {serviceType === "rcs" && shouldFetch && !isRcsFetching && !isRcsError && (
          <Box sx={{ px: 2, py: 1 }}>
            <Typography sx={{ fontSize: "0.75rem" }}>
              Total records: {totalRecords}
              {" · "}In date range:{" "}
              {rcsReportData?.summary?.total_in_date_range ?? 0}
              {" · "}Scheduled future:{" "}
              {rcsReportData?.summary?.total_scheduled_future ?? 0}
              {" · "}Pending overall:{" "}
              {rcsReportData?.summary?.total_pending_overall ?? 0}
            </Typography>
          </Box>
        )}
        {shouldFetch && totalPages > 1 && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              px: 2,
              py: 1,
            }}
          >
            <Pagination
              count={totalPages}
              page={page}
              onChange={handlePageChange}
              color="primary"
              shape="rounded"
              size="small"
            />
            <Typography sx={{ fontSize: "0.75rem" }}>
              Total records: {totalRecords}
            </Typography>
          </Box>
        )}
      </TableContainer>

      <Dialog
        open={textDialogOpen}
        onClose={() => setTextDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {serviceType === "rcs" ? "RCS Message Text" : "SMS Text"}
        </DialogTitle>
        <DialogContent dividers>
          <TextField
            multiline
            fullWidth
            minRows={4}
            value={selectedText}
            InputProps={{ readOnly: true }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTextDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ResellerScheduledSMS;
